import { isEmpty, isNull, isUndefined, omit, omitBy } from 'lodash';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import useBookVideoVisit from '~/components/AccountGetsMarried/components/GetCare/components/NavTiles/components/BookVideoVisit/hooks/useBookVideoVisit';
import { URGENT_CARE_VALUE } from '~/components/Home/Tiles/ProviderTypes';
import { getServiceIndexByServiceIds } from '~/components/Home/Tiles/Services';
import { PATIENT_TYPE_KIDS } from '~/constants';
import { COVID_SERVICE_IDS } from '~/constants/services';
import { CareType } from '~/core/dapi/careTeam';
import useSelectActiveUserProfile from '~/hooks/useSelectActiveUserProfile';
import useUniversalSearchByService from '~/hooks/useUniversalSearchByService';
import { CareOptionsParameters } from '~/routes/account/careTeam/CareOptionsPage';
import { REQUEST_UNIVERSAL_SEARCH } from '~/sagas/universalSearch';

export const URGENT_CARE_DOC = {
  type: 'provider-type',
  provider_type: URGENT_CARE_VALUE,
  name: 'Urgent Care',
  highlight: 'Urgent Care',
  fromSrp: false,
};

export function getSrpSearchParams({
  reasonForVisit,
  serviceIds,
  isCovidRelated,
  isFluRelated,
  isCovidTest,
  shouldReplaceUrl,
  patientType,
}: Omit<CareOptionsParameters, 'careType'>) {
  return omitBy(
    {
      type: isEmpty(serviceIds) ? 'provider-type' : 'service',
      provider_type: URGENT_CARE_VALUE,
      category: isEmpty(serviceIds) ? undefined : 'Labs and Tests',
      name: reasonForVisit ?? 'Urgent Care',
      highlight: reasonForVisit ?? 'Urgent Care',
      id: isEmpty(serviceIds) ? undefined : serviceIds?.join(','),
      is_covid_related: isCovidRelated,
      is_flu_related: isFluRelated,
      require_hard_filter: isCovidTest,
      fromSrp: false,
      should_replace_url: shouldReplaceUrl,
      patientType,
    },
    (v) => isUndefined(v) || isNull(v)
  );
}

/**
 * Goes directly to the SRP for a given care type.
 *
 * With regard to loading a video SRP:
 *
 * I do not entirely know how this hook works. I once tried to factor out the video-visit specific
 * logic from its nested `useBookVideoVisit` hook, and I failed. Something about the magic sauce
 * of the redux things that function does that should be unrelated to the SRP are somehow related
 * and I cannot figure out why.
 *
 * Alas, in place of a dangerous refactor of of the SRP's merging of url params and redux state,
 * (last time I played with this logic, I turned off public covid testing for search for the state
 * of Michigan for 4 hours :( -- https://solvhealth.slack.com/archives/C1K28S5EV/p1619015422254400)
 *
 * @returns a callback that will load the proper SRP type
 */
export function useSrpSearch(
  careType: CareType,
  queryParams?: Omit<CareOptionsParameters, 'careType'>
) {
  const userProfile = useSelectActiveUserProfile();

  const covidSearch = useUniversalSearchByService(getServiceIndexByServiceIds(COVID_SERVICE_IDS));
  const videoSearch = useBookVideoVisit(userProfile?.user_profile_id ?? '', null, false);

  const dispatch = useDispatch();

  /**
   * Function called when the user clicks the "continue to search" option
   */
  return useCallback(() => {
    switch (careType) {
      case 'uc':
        dispatch({
          type: REQUEST_UNIVERSAL_SEARCH,
          fullDoc: queryParams ? getSrpSearchParams(queryParams) : URGENT_CARE_DOC,
        });
        break;
      case 'video':
        videoSearch();
        break;
      case 'covid':
        covidSearch();
        break;
      default:
        break;
    }
  }, [careType, covidSearch, dispatch, queryParams, videoSearch]);
}
